import {
  AnalysisDTO,
  AnalysisUserType,
} from "@models/platform-analysis/entities/analysis";
import * as Yup from "yup";
import { UserType } from "@models/auth";
import { cloneDeep, isEmpty, xor } from "lodash";
import { AnalysisSetupDTO } from "@models/platform-analysis/entities/steps/setup";
import { AnalysisStep } from "@models/platform-analysis/enums/step";

export const getAnalysisStepSetupValidators = (userType: AnalysisUserType) =>
  userType === "supplier"
    ? analysisSetupSupplierValidator
    : analysisSetupAdviserValidator;

export const getUpdateAnalysisSetupData = ({
  setupData,
  originalData,
}: {
  setupData: AnalysisSetupDTO;
  originalData: AnalysisDTO;
}) => {
  const clonedSetupData = cloneDeep(setupData);
  const originalMainPlatformId = originalData.setup?.mainProductId;
  const newMainPlatformId = setupData.mainProductId;
  const originalOtherPlatformIds = originalData.setup?.otherProductIds || [];
  const newOtherPlatformIds = setupData.otherProductIds || [];

  clonedSetupData.hasRefreshData = !isEmpty(
    xor(
      [originalMainPlatformId, ...originalOtherPlatformIds],
      [newMainPlatformId, ...newOtherPlatformIds],
    ),
  );

  if (!setupData.hasFeeStep) {
    clonedSetupData.hasRateCard = false;
    clonedSetupData.selectRateCards = [];
  }

  return {
    updatedSetupData: clonedSetupData,
    updatedAnalysis: {
      ...originalData,
      currentStep: AnalysisStep.Setup,
      setup: clonedSetupData,
      feature: null,
      businessMetric: null,
      fee: null,
      summary: null,
    },
  };
};

// region private functions
const analysisSetupSupplierValidator = Yup.lazy((values) =>
  Yup.object().shape({
    ownerId: Yup.string().nullable(true).required("An owner is required"),
    name: Yup.string().nullable(true).required("Analysis must have a name"),
    adviserName: Yup.string()
      .nullable(true)
      .when("ownerType", (ownerType, schema) => {
        return ownerType === UserType.SupplierBdmSale &&
          !isEmpty((values.practiceName || "").trim())
          ? schema.required(
              "Adviser name is required when a practice name is provided",
            )
          : schema;
      }),
    practiceName: Yup.string()
      .nullable(true)
      .when("ownerType", (ownerType, schema) => {
        return ownerType === UserType.SupplierBdmSale &&
          !isEmpty((values.adviserName || "").trim())
          ? schema.required(
              "Practice name is required when an adviser name is provided",
            )
          : schema;
      }),
    mainProductId: Yup.string()
      .nullable(true)
      .when("hasMainProduct", (hasMainProduct, schema) => {
        return hasMainProduct
          ? schema.required("Main platform is required")
          : schema;
      }),
    hasSteps: Yup.array()
      .nullable(true)
      .min(1, "Select what you would like to include in the analysis"),
    otherProductIds: Yup.array()
      .nullable(true)
      .when("mainProductId", (mainProductId, schema) => {
        return schema
          .min(
            mainProductId ? 0 : 1,
            "Platform analysis requires at least one platform to be selected",
          )
          .max(
            mainProductId ? 4 : 5,
            "The maximum number of platforms is five",
          );
      }),
  }),
);

const analysisSetupAdviserValidator = Yup.lazy(() =>
  Yup.object().shape({
    ownerId: Yup.string().nullable(true).required("An adviser is required"),
    name: Yup.string().nullable(true).required("Review must have a name"),
    familyGroupId: Yup.string()
      .nullable(true)
      .required("Review must have a family group"),
    selectedFamilyMemberIds: Yup.array()
      .nullable(true)
      .when("familyGroupId", (_, schema) => {
        return schema
          .min(1, "The minimum member is one")
          .max(8, "The maximum member is eight");
      }),
    mainProductId: Yup.string()
      .nullable(true)
      .when("hasMainProduct", (hasMainProduct, schema) => {
        return hasMainProduct
          ? schema.required("Main platform is required")
          : schema;
      }),
    otherProductIds: Yup.array()
      .nullable(true)
      .when("mainProductId", (mainProductId, schema) => {
        return schema.min(
          mainProductId ? 0 : 1,
          "Platform analysis requires at least one platform to be selected",
        );
      }),
  }),
);
// endregion
